import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthLayout } from '../components/AppShell.js';
import { Alert, Button, TextField } from '../components/ui.js';
import { authClient } from '../lib/authClient.js';
import { confirmationOutcome } from '../lib/confirmationLink.js';
import { BRAND_CONTACT_EMAIL } from '../lib/brand.js';
import { useSession } from '../session/SessionProvider.js';

type ResendState = 'idle' | 'sending' | 'sent' | 'failed';

/**
 * Better Auth's answer when the password was right but the address has never
 * been confirmed. Only reachable once the password has been checked, so it
 * cannot be used to ask whether an address has an account.
 */
const EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED';

/**
 * The portal's own refusal, when the password was right but the **account** is
 * not one that may hold a session - D-14 Item 2.
 *
 * Raised by `databaseHooks.session.create.before` for an `invited` or a `closed`
 * agency, and it carries the sentence that names what to do next.
 *
 * **Shown verbatim, unlike a credential failure**, and that is a deliberate
 * exception to the rule below rather than a hole in it. Reaching this code means
 * the password was already checked and accepted, exactly as
 * `EMAIL_NOT_VERIFIED` does, so it tells a stranger nothing they did not
 * already know. Collapsing it into "that email address and password do not
 * match an account" would send somebody with a valid password and a waiting
 * invite to the password reset they do not need - which is precisely the
 * outcome these two sentences were written to prevent, and which they would
 * have failed to prevent while the browser was throwing them away.
 */
const ACCOUNT_NOT_ACTIVE = 'ACCOUNT_NOT_ACTIVE';

/** Said when the server refused for this reason but sent no sentence with it. */
const ACCOUNT_NOT_ACTIVE_FALLBACK =
  'This account cannot be signed into. Contact support and we will sort it out.';

export function SignInPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Set when sign-in was refused only because the address is unconfirmed. */
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resend, setResend] = useState<ResendState>('idle');

  // Where the confirmation link lands. `error` is appended by Better Auth when
  // the token was expired or already spent.
  // Read as the signed-in dashboard reads it (C-124), so the two cannot disagree.
  const outcome = confirmationOutcome(`?${searchParams.toString()}`);
  const justVerified = outcome !== null;
  const verificationFailed = outcome === 'failed';

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setUnverifiedEmail(null);
    setResend('idle');
    setBusy(true);

    const address = email.trim();

    try {
      const result = await authClient.signIn.email({ email: address, password });

      if (result.error) {
        if (result.error.code === EMAIL_NOT_VERIFIED) {
          setUnverifiedEmail(address);
          return;
        }
        /*
         * D-14 Item 2. The server's own sentence, shown as written, because it
         * names what to do next and nothing else does. See ACCOUNT_NOT_ACTIVE.
         */
        if (result.error.code === ACCOUNT_NOT_ACTIVE) {
          setError(result.error.message ?? ACCOUNT_NOT_ACTIVE_FALLBACK);
          return;
        }
        // Deliberately the same message for a wrong password and an unknown
        // address, so this page cannot be used to discover who has an account.
        setError('That email address and password do not match an account.');
        return;
      }

      await refresh();
      void navigate('/', { replace: true });
    } catch {
      setError('We could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleResend(): Promise<void> {
    if (!unverifiedEmail) return;
    setResend('sending');
    try {
      /*
       * C-97. Better Auth's client answers a refusal with `error` rather than
       * by throwing, so this used to say "on its way" to every answer. The
       * server now refuses when the email really did not go, and that refusal
       * is shown as the failure it is.
       */
      const result = await authClient.sendVerificationEmail({ email: unverifiedEmail });
      setResend(result.error ? 'failed' : 'sent');
    } catch {
      setResend('failed');
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Manage your credits, orders and sites."
      footer={
        <>
          Don&rsquo;t have an account?{' '}
          <Link to="/sign-up" className="font-medium text-fg underline">
            Create one
          </Link>
        </>
      }
    >
      <form onSubmit={(event) => void handleSubmit(event)} className="space-y-5" noValidate>
        {verificationFailed ? (
          <Alert>
            That confirmation link is no longer valid. It may have expired or already been used.
            Sign in below and we will send you a new one.
          </Alert>
        ) : justVerified ? (
          <Alert tone="success">Your email address is confirmed. You can sign in now.</Alert>
        ) : null}

        {error ? <Alert>{error}</Alert> : null}

        {unverifiedEmail ? (
          <Alert tone="info">
            <p>
              Confirm your email address before signing in. We sent a link to{' '}
              <strong>{unverifiedEmail}</strong> when the account was created.
            </p>
            {resend === 'sent' ? (
              <p className="mt-2">A new confirmation link is on its way.</p>
            ) : resend === 'failed' ? (
              <p className="mt-2">
                We could not send it just now. Please try again in a moment.{' '}
                <button
                  type="button"
                  onClick={() => void handleResend()}
                  className="font-medium underline"
                >
                  Resend confirmation email
                </button>
                {/* C-66: somewhere to go when retrying is not working. */}
                <span className="mt-2 block">
                  If this keeps happening, write to {BRAND_CONTACT_EMAIL}.
                </span>
              </p>
            ) : (
              <p className="mt-2">
                <button
                  type="button"
                  onClick={() => void handleResend()}
                  disabled={resend === 'sending'}
                  className="font-medium underline disabled:no-underline disabled:opacity-60"
                >
                  {resend === 'sending' ? 'Sending…' : 'Resend confirmation email'}
                </button>
              </p>
            )}
          </Alert>
        ) : null}

        <TextField
          label="Email address"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <TextField
          label="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <div className="flex items-center justify-between">
          <Link to="/forgot-password" className="text-sm text-fg-muted underline">
            Forgotten your password?
          </Link>
        </div>

        <Button type="submit" busy={busy} className="w-full">
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
